import { Component, effect, inject, input, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, switchMap } from 'rxjs/operators';
import { RegistrierungService } from '../../services/registrierung.service';
import { GewerkeItem } from '../../model/gewerk';
import { MatCheckbox } from '@angular/material/checkbox';
import { MarktplatzdatenMTO } from '../../generated-model/marktplatzdatenMTO';
import { gewerk, umkreis } from '../../model/konstanten';
import { NgOptimizedImage } from '@angular/common';
import { MatButton } from '@angular/material/button';
import { SelectorButtonDirective } from '@adcl/ngx-material';
import { UmkreisItem } from '../../model/Umkreis';
import { TranslatePipe } from '@ngx-translate/core';
import { RegistrierungComponent } from '../registrierung.component';

@Component({
  selector: 'app-marktplatz-bedingungen',
  imports: [MatCheckbox, ReactiveFormsModule, NgOptimizedImage, MatButton, SelectorButtonDirective, TranslatePipe],
  templateUrl: './marktplatz-bedingungen.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './marktplatz-bedingungen.component.scss',
})
export class MarktplatzBedingungenComponent {
  private readonly registrierungService = inject(RegistrierungService);
  protected registrierungComponent = inject(RegistrierungComponent);

  // Signal inputs and state
  formGroupMarktplatzbedingungen = input<FormGroup>({} as FormGroup);
  gewerke = signal<GewerkeItem[]>([]);
  umkreise = signal<UmkreisItem[]>([]);
  marktplatzDaten = signal<MarktplatzdatenMTO>({} as MarktplatzdatenMTO);

  constructor() {
    // Initialize data
    this.initializeGewerke();
    this.initializeUmkreis();
    const umkreisNull = this.umkreise().find((u) => u.value === '0');
    if (umkreisNull) {
      umkreisNull.checked = true;
    }

    // Setup auto-save effect on form value changes
    effect(() => {
      const fg = this.formGroupMarktplatzbedingungen();
      if (fg?.valueChanges) {
        fg.valueChanges
          .pipe(
            debounceTime(200),
            switchMap((value) => {
              return this.updateMarktplatz(value);
            })
          )
          .subscribe((result) => {
            if (result === false) {
              console.log('formular failure: autosave not executed');
            }
          });
      }
    });
  }

  private updateMarktplatz(value: any) {
    const marktplatz = this.marktplatzDaten();
    if (value !== 'gewerk change' && value !== 'umkreis change') {
      marktplatz.km_bereich = value.km_bereich;
      marktplatz.einverstanden = value.einverstanden;
      if (!marktplatz.einverstanden) {
        this.initializeGewerke();
        marktplatz.gewerke = [];
        marktplatz.km_bereich = 0;
      }
    } else if (value === 'gewerk change') {
      marktplatz.gewerke = this.getSelectedGewerkeValues();
    }
    this.marktplatzDaten.set(marktplatz);
    this.registrierungService.registrierungsdaten.marktplatz = marktplatz;
    return this.registrierungService.saveRegistrierungsdatenToLocalStorage();
  }

  initializeGewerke() {
    this.gewerke.set(gewerk.map((gewerk) => ({ ...gewerk, checked: false })));
  }

  initializeUmkreis() {
    this.umkreise.set(umkreis.map((umkreis) => ({ ...umkreis, checked: false })));
  }

  onGewerkeChange(item: GewerkeItem) {
    if (item.checked == undefined) {
      item.checked = true;
    } else {
      item.checked = !item.checked;
    }
    // Trigger manual update
    const marktplatz = this.marktplatzDaten();
    marktplatz.gewerke = this.getSelectedGewerkeValues();
    this.marktplatzDaten.set(marktplatz);
    this.registrierungService.registrierungsdaten.marktplatz = marktplatz;
    this.registrierungService.saveRegistrierungsdatenToLocalStorage().subscribe();
  }

  onUmkeisChange(item: UmkreisItem) {
    this.umkreise().forEach((umkreis) => (umkreis.checked = false));
    const selectedUmkreis = this.umkreise().find((umkreis) => umkreis.value === item.value);
    if (selectedUmkreis) {
      selectedUmkreis.checked = true;
      const marktplatz = this.marktplatzDaten();
      marktplatz.km_bereich = Number.parseInt(item.value, 10);
      this.marktplatzDaten.set(marktplatz);
      this.registrierungService.registrierungsdaten.marktplatz = marktplatz;
      this.registrierungService.saveRegistrierungsdatenToLocalStorage().subscribe();
    }
  }

  getSelectedGewerkeValues(): string[] {
    return this.gewerke()
      .filter((item) => item.checked)
      .map((item) => item.value);
  }

  isEinverstanden(): boolean {
    const einverstandenValue = this.formGroupMarktplatzbedingungen().get('einverstanden')?.value;
    if (this.registrierungService.registrierungsdaten.marktplatz) {
      this.registrierungService.registrierungsdaten.marktplatz.einverstanden = einverstandenValue;
    }
    return einverstandenValue;
  }

  isMarktPlatzAusgeschlossen(): boolean {
    return this.registrierungComponent.registrierungOhneMarktPlatzRelation();
  }

  isTestUmgebung() {
    return this.registrierungComponent.testUmgebung();
  }
}
